import request, { authOptions } from 'api/requestUtils'
import type { paths } from '@luxuryescapes/contract-svc-membership'
import asyncPoll from 'lib/promise/asyncPoll'
import { SUPPORTED_BENEFIT_TYPES } from 'luxPlus/constants/benefits'
import { PARTNER_PROMO_CLASSIFIER_VALUES } from 'luxPlus/constants/partnerPromo'
import { dateIsAfterOrSame } from 'lib/datetime/dateUtils'
import { last, sortBy } from 'lib/array/arrayUtils'
import { ensureLuxPlusBenefitTitle } from 'lib/checkout/luxPlusSubscription/benefitMappers'

const BASE_URI = '/api/membership'

/**
 * This section is all about getting the redeemed benefits and potential savings by order id.
 */
type RedeemedBenefitsResponse = paths['/api/membership/redeem-benefits/order/{orderId}']['get']['responses'][200]['content']['application/json']
type PotentialSavingsResponse = paths['/api/membership/potential-order-savings']['get']['responses'][200]['content']['application/json']

export async function getRedeemedBenefitsByOrderId(orderId: string) {
  const response = await request.get<RedeemedBenefitsResponse>(
    `/api/membership/redeem-benefits/order/${orderId}`,
    { ...authOptions(), excludeBrand: true },
  )
  return response.result
}

type GetPotentialSavingsParams = {
  customerId: string
  region: string
  orderId: string
} | {
  customerId: string
  region: string
  daysPeriod: string
}

export async function getPotentialSavings(params: GetPotentialSavingsParams) {
  const queryParams = new URLSearchParams(params)
  const response = await request.get<PotentialSavingsResponse>(
    `/api/membership/potential-order-savings?${queryParams}`,
    { ...authOptions(), excludeBrand: true },
  )
  return response.result
}

export async function getTotalPotentialSavings(customerId: string, region: string, daysPeriod = 365): Promise<App.LuxPlusTotalPotentialSavings> {
  const params = new URLSearchParams({ customerId, region, daysPeriod: String(daysPeriod) })
  const response = await request.get<PotentialSavingsResponse>(
    `/api/membership/potential-order-savings?${params}`,
    { ...authOptions(), excludeBrand: true },
  )
  const result = response.result
  return {
    totalAmount: result.totalDisplayCurrencyAmount || 0,
    items: (result.items ?? []).map(item => ({
      paymentDate: item.paymentDate,
      displayMemberPrice: item.displayMemberPrice,
      displayNonMemberPrice: item.displayNonMemberPrice,
      displayAmount: item.displayAmount,
      displayCurrency: item.displayCurrency,
      itemId: item.itemId,
      orderId: item.orderId,
    })),
  }
}

type MembershipMetricsApiResponse = paths['/api/membership/membership-metrics']['get']['responses'][200]['content']['application/json']

export async function getMembershipMetrics(region: string): Promise<App.LuxPlusMembershipMetrics> {
  const params = new URLSearchParams({ region })
  const response = await request.get<MembershipMetricsApiResponse>(
    `${BASE_URI}/membership-metrics?${params}`,
    { excludeBrand: true },
  )
  return {
    trustedByCount: response.result.trustedByCount,
  }
}

/**
  * This section is all about getting and the subscription offer and mapping it to be used in the customer portal.
*/
type SubscriptionOfferResponse = paths['/api/membership/subscription-offers']['get']['responses'][200]['content']['application/json']
type SubscriptionOffer = SubscriptionOfferResponse['result'][number]
type SubscriptionOfferBenefit = SubscriptionOffer['benefits'][number]

function isSupportedBenefit({ type }: SubscriptionOfferBenefit | CustomerSubscriptionBenefit): boolean {
  return SUPPORTED_BENEFIT_TYPES.includes(type)
}

function toPartnerPromoClassifier(value?: string): App.LuxPlusPartnerPromoClassifier | undefined {
  return value && PARTNER_PROMO_CLASSIFIER_VALUES.has(value) ? value as App.LuxPlusPartnerPromoClassifier : undefined
}

function mapSubscriptionOfferBenefit(serverBenefit: SubscriptionOfferBenefit): App.OfferSubscriptionBenefitItem {
  return ensureLuxPlusBenefitTitle({
    type: serverBenefit.type,
    title: serverBenefit.title,
    shortTitle: serverBenefit.shortTitle,
    description: serverBenefit.description,
    usedAmount: 0,
    allocatedAmount: serverBenefit.allocatedAmount,
    classifier: toPartnerPromoClassifier(serverBenefit.classifier),
  })
}

function mapServerSubscriptionToLocal(serverSubscription: SubscriptionOffer): App.SubscriptionOffer {
  return {
    type: 'subscription',
    id: serverSubscription.id,
    price: serverSubscription.price,
    region: serverSubscription.region,
    benefits: serverSubscription.benefits.filter(isSupportedBenefit).map(mapSubscriptionOfferBenefit),
    tier: serverSubscription.tier ?? 'base',
    joinFee: serverSubscription.joiningFeeValue,
    joinFeeWaived: serverSubscription.joiningFee === 0,
    attachmentBands: (serverSubscription.attachmentBands ?? []) as Array<[number, number]>,
    status: serverSubscription.status,
  }
}

export async function getSubscriptionOffer(region: string): Promise<Array<App.SubscriptionOffer>> {
  const params = new URLSearchParams({ region })
  const offer = await request.get<SubscriptionOfferResponse>(`/api/membership/subscription-offers?${params}`)
  return offer.result.map(mapServerSubscriptionToLocal)
}

/**
  * This section is all about getting and the customer subscriptions offer and mapping it to be used in the customer portal.
*/
type CustomerSubscriptionResponse = paths['/api/membership/customer-subscriptions']['get']['responses'][200]['content']['application/json']
type CustomerSubscription = CustomerSubscriptionResponse['result']
type CustomerSubscriptionBenefit = CustomerSubscription['currentBenefits'][0]
type RedeemedBenefit = CustomerSubscription['subscriptionPeriods'][0]['redeemedBenefits'][0]

function mapCustomerSubscriptionBenefit(serverBenefit: CustomerSubscriptionBenefit): App.MemberSubscriptionBenefitItem {
  return {
    type: serverBenefit.type,
    title: serverBenefit.title ?? '',
    shortTitle: serverBenefit.shortTitle ?? '',
    description: serverBenefit.description,
    allocatedAmount: serverBenefit.allocatedAmount ?? undefined,
    usedAmount: serverBenefit.usedAmount ?? undefined,
    unit: serverBenefit.unit ?? undefined,
    classifier: toPartnerPromoClassifier(serverBenefit.classifier),
    code: serverBenefit.code,
    expirationDate: serverBenefit.expirationDate,

  }
}

function mapRedeemedBenefit(redeemedBenefit: RedeemedBenefit): App.RedeemedMembershipBenefit {
  return {
    id: redeemedBenefit.id,
    type: redeemedBenefit.type as App.LuxPlusBenefitType,
    subtype: redeemedBenefit.subtype ?? undefined,
    status: redeemedBenefit.status,
    description: redeemedBenefit.description ?? undefined,
    adjustment: redeemedBenefit.adjustment,
    unit: redeemedBenefit.unit,
    orderId: redeemedBenefit.orderId,
    itemType: redeemedBenefit.itemType,
    itemId: redeemedBenefit.itemId,
    updatedAt: redeemedBenefit.updatedAt,
  }
}

function mapCurrentRenewalOffer(
  serverRenewalOffer:
    | { id: string; price: number; tier: App.MembershipSubscriptionTier; region: App.LuxPlusRegion }
    | null
    | undefined,
): App.MemberRenewalOffer | undefined {
  if (!serverRenewalOffer) return undefined
  return {
    id: serverRenewalOffer.id,
    price: serverRenewalOffer.price,
    tier: serverRenewalOffer.tier,
    region: serverRenewalOffer.region,
  }
}

function mapCustomerSubscription(serverCustomerSubscription: CustomerSubscription): App.MemberSubscriptionItem {
  const activePeriod = serverCustomerSubscription.subscriptionPeriods.find(period => period.status === 'ACTIVE')
  const currentSubscriptionPeriod = sortBy(serverCustomerSubscription.subscriptionPeriods, (period) => new Date(period.startDate), 'desc')[0]
  const now = new Date()

  // TODO - fix contract......
  const upcomingRenewalActions = currentSubscriptionPeriod?.renewalActions?.filter((renewalAction) => dateIsAfterOrSame(new Date(renewalAction.actionDate), now))?.map((renewalAction) => renewalAction.actionDate) ?? []

  if (!upcomingRenewalActions.length) {
    upcomingRenewalActions.push(serverCustomerSubscription.expiryDate)
  }

  const sortedUpcomingRenewalActions = sortBy(
    upcomingRenewalActions,
    (actionDate) => new Date(actionDate),
    'asc',
  )
  return {
    id: serverCustomerSubscription.id,
    status: serverCustomerSubscription.status,
    region: serverCustomerSubscription.region,
    benefits: serverCustomerSubscription.currentBenefits.filter(isSupportedBenefit).map(mapCustomerSubscriptionBenefit),
    renewalStatus: serverCustomerSubscription.renewalStatus,
    payRenewalWithPoints: serverCustomerSubscription.payRenewalWithPoints ?? false,
    memberSince: serverCustomerSubscription.memberSince,
    expiryDate: serverCustomerSubscription.expiryDate,
    tier: serverCustomerSubscription.tier,
    redeemedBenefits: serverCustomerSubscription.subscriptionPeriods.flatMap(period => period.redeemedBenefits.map(mapRedeemedBenefit)),
    isWithinRenewalGracePeriod: serverCustomerSubscription.isWithinRenewalGracePeriod,
    currentRenewalOffer: mapCurrentRenewalOffer(serverCustomerSubscription.currentRenewalOffer),
    activeSubscription: activePeriod ? {
      id: activePeriod.id,
      redeemedBenefits: activePeriod.redeemedBenefits.map(mapRedeemedBenefit),
      offerId: activePeriod.subscriptionOffer.id,
      price: activePeriod.subscriptionOffer.price,
      startDate: activePeriod.startDate,
    } : undefined,
    currentSubscriptionPrice: currentSubscriptionPeriod?.subscriptionOffer.price,
    currentSubscriptionOfferId: currentSubscriptionPeriod?.subscriptionOffer.id,
    currentPeriodStartDate: currentSubscriptionPeriod?.startDate,
    nextChargeAttemptDate: sortedUpcomingRenewalActions[0],
    finalChargeAttemptDate: sortedUpcomingRenewalActions.length ? last(sortedUpcomingRenewalActions) : undefined,
    renewalRewardEligibleYears: serverCustomerSubscription.renewalRewardEligibleYears,
  }
}

/**
 * Retrieves the member subscription item for a given customer ID.
 *
 * @param {string} customerId - The ID of the customer.
 * @returns {Promise<App.MemberSubscriptionItem>} - A promise that resolves to the member subscription item.
 */
interface GetMemberSubscriptionParams {
  customerId: string
  regionCode: string
  accessToken?: string
}

export interface MemberSubscriptionResult {
  item?: App.MemberSubscriptionItem
  lastFreePreviews?: Array<App.LuxPlusLastFreePreview>
}

export type MemberSubscriptionEligibilityResult =
  | ({ outcome: 'success' } & MemberSubscriptionResult)
  | { outcome: 'no-subscription' }

function isMembershipNotFound(error: unknown): boolean {
  return !!error && typeof error === 'object' && 'status' in error && error.status === 404
}

async function requestMemberSubscription({
  customerId,
  accessToken,
}: GetMemberSubscriptionParams): Promise<MemberSubscriptionResult> {
  const params = new URLSearchParams({ customerId, includePreviewHistory: 'true' })
  const subscription = await request.get<CustomerSubscriptionResponse>(`${BASE_URI}/customer-subscriptions?${params}`, authOptions(accessToken))
  const result = subscription.result
  const lastFreePreviews = result.latestPreviewPeriods?.map((period): App.LuxPlusLastFreePreview => ({
    region: period.region,
    startDate: period.startDate,
    endDate: period.endDate,
    status: period.status,
  }))

  const isFinishedPreviewOnly = result.tier === 'free_preview' && result.status !== 'ACTIVE'
  return {
    item: isFinishedPreviewOnly ? undefined : mapCustomerSubscription(result),
    lastFreePreviews,
  }
}

export async function getMemberSubscription(params: GetMemberSubscriptionParams): Promise<MemberSubscriptionResult> {
  try {
    return await requestMemberSubscription(params)
  } catch(_e) {
    // server respond with a 404 when there's no subscription available
    // We don't want to treat this as an error but instead as "no subscription"
    // (other errors have always resolved as "no subscription" here too — kept that way)
    return {}
  }
}

/**
 * Strict subscription read for cart cleanup and checkout revalidation.
 * A 404 is "no subscription". Network and 5xx errors reject so callers do not treat them as membership ending.
 */
export async function getMemberSubscriptionEligibility(params: GetMemberSubscriptionParams): Promise<MemberSubscriptionEligibilityResult> {
  try {
    const result = await requestMemberSubscription(params)
    return { outcome: 'success', ...result }
  } catch(error) {
    if (isMembershipNotFound(error)) {
      return { outcome: 'no-subscription' }
    }
    throw error
  }
}

/**
 * This function is used to retrieve the member subscription item by polling until the status is not 'PENDING'.
 * It should only be used when purchasing a new membership to get the newly purchased subscription.
 *
 * @param {string} customerId - The ID of the customer.
 * @returns {Promise<App.MemberSubscriptionItem>} - A promise that resolves to the member subscription item.
 */
export async function getMemberSubscriptionPoll(
  customerId: string,
  regionCode: string,
  accessToken?: string,
  validate?: (result: App.MemberSubscriptionItem) => boolean,
): Promise<MemberSubscriptionResult | undefined> {
  return await asyncPoll<MemberSubscriptionResult | undefined>({
    validateFunction: async(result) => {
      if (!result?.item) {
        return false
      }
      if (validate) return validate(result.item)
      return result.item.status !== 'CONFIRMED_AWAITING_PAYMENT'
    },
    apiCall: () => getMemberSubscription({ customerId, regionCode, accessToken }),
    maxTime: 10000,
    pollInterval: 1000,
  })
}

type CustomerSubscriptionUpdate = paths['/api/membership/customer-subscriptions']['put']
type CustomerSubscriptionUpdatePayload = CustomerSubscriptionUpdate['requestBody']['content']['application/json']
type CustomerSubscriptionUpdateResponse = CustomerSubscriptionUpdate['responses'][200]['content']['application/json']

export type MemberSubscriptionUpdateData =
  | (Required<Pick<CustomerSubscriptionUpdatePayload, 'op'>> & Pick<CustomerSubscriptionUpdatePayload, 'reason'>)
  | Required<Pick<CustomerSubscriptionUpdatePayload, 'payRenewalWithPoints'>>

export async function updateMemberSubscription(customerId: string, data: MemberSubscriptionUpdateData): Promise<App.MemberSubscriptionItem> {
  const payload: CustomerSubscriptionUpdatePayload = { customerId, ...data }
  const subscription = await request.put<CustomerSubscriptionUpdateResponse, CustomerSubscriptionUpdatePayload>(`${BASE_URI}/customer-subscriptions?without_brand=1`, payload, { credentials: 'include' })
  return mapCustomerSubscription(subscription.result)
}

interface CreateFreePreviewMemberPayload {
  customerId: string
  region: string
}

/**
 * This function is used to create a free preview period for a member.
 *
 * @param {CreateFreePreviewMemberPayload} payload - The payload containing the customer ID, brand and region.
 * @returns {Promise<App.MemberSubscriptionItem>} - A promise that resolves to the member subscription item.
 */
export async function createMemberFreePreviewPeriod(payload: CreateFreePreviewMemberPayload, accessToken?: string): Promise<App.MemberSubscriptionItem> {
  const freePreviewPeriod = await request.post<CustomerSubscriptionResponse, CreateFreePreviewMemberPayload>(`${BASE_URI}/preview-periods`, payload, authOptions(accessToken))
  return mapCustomerSubscription(freePreviewPeriod.result)
}
